/**
 * Stone Investment View Switcher
 * 
 * Toggle between grid and list views with Stone Investment's luxury aesthetic:
 * - Greyscale colour palette
 * - Subtle transitions
 */

'use client';

import { Grid3X3, List } from 'lucide-react';

export type ViewMode = 'grid' | 'list';

export interface StoneViewSwitcherProps {
  /**
   * Current view mode
   */
  value: ViewMode;
  /**
   * Callback when view mode changes
   */
  onChange: (mode: ViewMode) => void;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Labels for accessibility
   */
  labels?: {
    gridView?: string;
    listView?: string;
  };
  /**
   * Translation messages
   */
  messages?: Record<string, unknown>;
}

/**
 * StoneViewSwitcher - Grid/List toggle with Stone Investment styling
 */
export function StoneViewSwitcher({
  value,
  onChange,
  className = '',
  labels = {},
  messages,
}: StoneViewSwitcherProps) {
  // Get stone-specific translations
  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const viewSwitcherMessages = (stoneMessages.viewSwitcher || {}) as Record<string, string>;
  
  // Resolved labels
  const resolvedLabels = {
    gridView: labels.gridView || viewSwitcherMessages.gridView || 'Grid view',
    listView: labels.listView || viewSwitcherMessages.listView || 'List view',
  };
  
  return (
    <div className={`inline-flex border border-stone-300 ${className}`}>
      <button
        onClick={() => onChange('grid')}
        className={`
          flex items-center justify-center w-10 h-10 transition-colors
          ${value === 'grid' 
            ? 'bg-stone-900 text-white' 
            : 'bg-white text-stone-500 hover:text-stone-700 hover:bg-stone-50'
          }
        `}
        aria-label={resolvedLabels.gridView}
        aria-pressed={value === 'grid'}
      >
        <Grid3X3 size={18} strokeWidth={1.5} />
      </button>
      
      <button
        onClick={() => onChange('list')}
        className={`
          flex items-center justify-center w-10 h-10 transition-colors border-l border-stone-300
          ${value === 'list' 
            ? 'bg-stone-900 text-white' 
            : 'bg-white text-stone-500 hover:text-stone-700 hover:bg-stone-50'
          }
        `}
        aria-label={resolvedLabels.listView}
        aria-pressed={value === 'list'}
      >
        <List size={18} strokeWidth={1.5} />
      </button>
    </div>
  );
}

export default StoneViewSwitcher;
